/**
 * GenerateSingleInvoiceModal
 * Modal for generating a single invoice for one affaire
 * Supports product selection, partial invoicing, and discount codes
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  GlobeAltIcon,
  MinusIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  PaperAirplaneIcon,
  EyeIcon,
  EnvelopeIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import {
  useInvoiceEligibility,
  useGenerateSingleInvoice,
  useNextInvoiceNumber,
  useValidateInvoice,
} from '../../hooks/useInvoices';
import { useBillingContacts } from '../../hooks/useProducts';
import { useBankAccounts, useAddBankAccount } from '../../hooks/useBankAccounts';
import type { BillingContact, BankAccount } from '../../types/facturation.types';
import { usePeppolRegistration, useValidateVat } from '../../hooks/useVatValidation';
import { downloadInvoicePdf } from '../../api/facturation.api';
import { COUNTRY_OPTIONS, toCountryCode } from '../../constants/countries.constants';
import type { VatValidation, AffaireProduct, ProductSelection, Invoice } from '../../types/facturation.types';
import { useAppStore } from '@/app/store';
import { PdfPreviewModal } from '../PdfPreviewModal/PdfPreviewModal';
import { ModalErrorBoundary } from '../ErrorBoundary';
import { VatDataComparison } from './components/VatDataComparison';
import '../../facturation.css';

/**
 * Check if a VAT number is valid (not empty, not "NA", not "N/A", etc.)
 * Used to skip VIES validation for non-professional contacts
 */
const isValidVatNumber = (vatNumber: string | null | undefined): boolean => {
  if (!vatNumber || typeof vatNumber !== 'string') return false;
  const cleaned = vatNumber.trim().toUpperCase();
  return cleaned !== '' && cleaned !== 'NA' && cleaned !== 'N/A' && cleaned !== '-' && cleaned !== 'NÉANT';
};

interface GenerateSingleInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  affaireId: string;
  dossierNumber?: string;
  onSuccess?: () => void;
}

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

// Extract a human-readable error message from an axios error.
// Backend errors come back as { success: false, error: { code, message } }; some
// older endpoints flatten that to { message }. axios' own error.message is the
// generic "Request failed with status code 400", so we prefer the server text.
const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  const e = error as {
    response?: { data?: { error?: { message?: string }; message?: string } };
    message?: string;
  };
  return (
    e?.response?.data?.error?.message ||
    e?.response?.data?.message ||
    (error instanceof Error ? error.message : '') ||
    fallback
  );
};

// Language options
const languages = [
  { code: 'fr', label: 'Francais' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];

// Discount type
interface Discount {
  code: string;
  type: 'percentage' | 'fixed';
  value: number;
  description?: string;
}

export const GenerateSingleInvoiceModal: React.FC<GenerateSingleInvoiceModalProps> = ({
  isOpen,
  onClose,
  affaireId,
  dossierNumber,
  onSuccess
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState('fr');
  const [vatValidationResult, setVatValidationResult] = useState<VatValidation | null>(null);
  const [step, setStep] = useState<'check' | 'confirm' | 'generating' | 'preview' | 'finalizing' | 'success' | 'error'>('check');
  const [errorMessage, setErrorMessage] = useState('');
  const [generatedInvoice, setGeneratedInvoice] = useState<Invoice | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [isPdfPreviewMode, setIsPdfPreviewMode] = useState(false); // true = draft preview mode
  const [sendEmailOnValidate, setSendEmailOnValidate] = useState(true); // Option to send email on validation
  const [vatOverrideAccepted, setVatOverrideAccepted] = useState(false); // Override for invalid VAT

  // Product selection state - now with quantities for partial invoicing
  // RACE CONDITION FIX: Separate user modifications from computed initial values
  // userModifiedQuantities tracks explicit user changes (clicking +/-, typing, toggling)
  // This prevents product refetches from resetting user's intentional modifications
  const [userModifiedQuantities, setUserModifiedQuantities] = useState<Map<number, number>>(new Map());

  // Track the last affaireId to detect when we need to reset user modifications
  const lastAffaireIdRef = useRef<string | null>(null);

  // Track products that became unavailable mid-session (removed or fully invoiced)
  const [unavailableProducts, setUnavailableProducts] = useState<Map<number, { description: string; reason: 'removed' | 'fully_invoiced' }>>(new Map());

  // Track if quantities have been adjusted due to remaining quantity changes
  const [quantitiesAdjusted, setQuantitiesAdjusted] = useState(false);

  // Ref to track previously available product IDs for detecting changes
  const previousAvailableIdsRef = useRef<Set<number>>(new Set());

  // Discount state
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValue, setDiscountValue] = useState<number>(0);

  // Split by parts state
  const [splitParts, setSplitParts] = useState<number | null>(null);

  // Selected billing contact state (for when multiple contacts exist)
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);

  // Editable billing address (pre-filled from the contact's FileMaker data, overridable before generating)
  const [billingAddressEdit, setBillingAddressEdit] = useState<{ address: string; postalCode: string; city: string; countryCode: string }>(
    { address: '', postalCode: '', city: '', countryCode: 'BE' }
  );
  // Address editor hidden by default — revealed via the "Adresse" edit button in the header
  const [showAddressEdit, setShowAddressEdit] = useState(false);
  // 0%-VAT operator override: null = use auto-detected default, true/false = explicit operator choice
  const [zeroVatOverride, setZeroVatOverride] = useState<boolean | null>(null);

  // Bank account state
  const [bankAccountId, setBankAccountId] = useState<number | null>(null);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [newAccountLabel, setNewAccountLabel] = useState('');
  const [newAccountIban, setNewAccountIban] = useState('');
  const [newAccountBic, setNewAccountBic] = useState('');

  // Hooks
  const { data: eligibility, isLoading: isCheckingEligibility } = useInvoiceEligibility(affaireId);
  const { data: billingContacts = [], isError: billingContactsError, isLoading: billingContactsLoading, refetch: billingContactsRefetch } = useBillingContacts(affaireId);
  const { data: nextNumber } = useNextInvoiceNumber();

  // Get the selected contact or default to first one
  const billingContact: BillingContact | undefined = useMemo(() => {
    if (billingContacts.length === 0) return undefined;
    if (selectedContactId) {
      return billingContacts.find(c => c.contact_id === selectedContactId) || billingContacts[0];
    }
    return billingContacts[0];
  }, [billingContacts, selectedContactId]);

  // Pre-fill the editable billing address from the contact's FileMaker data when it changes
  useEffect(() => {
    if (billingContact) {
      setBillingAddressEdit({
        address: billingContact.street || billingContact.address || '',
        postalCode: billingContact.code_postal || '',
        city: billingContact.ville || '',
        countryCode: toCountryCode(billingContact.pays),
      });
      setZeroVatOverride(null); // re-derive the 0% default for the new contact
    }
  }, [billingContact?.contact_id]);

  const { data: peppolStatus } = usePeppolRegistration(
    billingContact?.vat_number || ''
  );

  const { data: bankAccounts = [] } = useBankAccounts();
  const addBankAccountMutation = useAddBankAccount();

  const generateMutation = useGenerateSingleInvoice();
  const validateMutation = useValidateInvoice();
  const validateVatMutation = useValidateVat();
  const { addNotification } = useAppStore();

  // Ref to store the mutation function to avoid dependency issues
  const validateVatMutateRef = useRef(validateVatMutation.mutate);
  validateVatMutateRef.current = validateVatMutation.mutate;

  // Get enriched products from eligibility (includes invoiced amounts)
  const enrichedProducts = useMemo(() => {
    return (eligibility?.products || []) as AffaireProduct[];
  }, [eligibility?.products]);

  // Get available products (those not fully invoiced)
  const availableProducts = useMemo(() => {
    return enrichedProducts.filter(p => !p.fully_invoiced);
  }, [enrichedProducts]);

  // Get fully invoiced products for display
  const fullyInvoicedProducts = useMemo(() => {
    return enrichedProducts.filter(p => p.fully_invoiced);
  }, [enrichedProducts]);

  // Compute effective quantities by merging initial values with user modifications
  // RACE CONDITION FIX: This computed value separates user modifications from initial values.
  // Initial value: remaining_quantity (or quantity if no remaining) - all selected by default
  // User modification: overrides the initial value for that product
  // When products refetch, user modifications are preserved unless quantity exceeds remaining
  const productQuantities = useMemo(() => {
    const merged = new Map<number, number>();
    availableProducts.forEach(p => {
      const userQty = userModifiedQuantities.get(p.id);
      if (userQty !== undefined) {
        // User has explicitly set this quantity - use it (capped at remaining)
        const maxQty = p.remaining_quantity ?? p.quantity;
        merged.set(p.id, Math.min(userQty, maxQty));
      } else {
        // No user modification - use remaining quantity as default (all selected)
        const remaining = p.remaining_quantity ?? p.quantity;
        merged.set(p.id, remaining > 0 ? remaining : 0);
      }
    });
    return merged;
  }, [availableProducts, userModifiedQuantities]);

  // Ref to access current productQuantities without adding it to dependencies
  const productQuantitiesRef = useRef<Map<number, number>>(new Map());
  productQuantitiesRef.current = productQuantities;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('check');
      setErrorMessage('');
      setVatValidationResult(null);
      setDiscountType('percentage');
      setDiscountValue(0);
      setSplitParts(null);
      setUserModifiedQuantities(new Map());  // Clear user modifications
      setGeneratedInvoice(null);
      setIsDownloading(false);
      setIsValidating(false);
      setSelectedLanguage('fr');
      setUnavailableProducts(new Map());
      setQuantitiesAdjusted(false);
      setSelectedContactId(null);  // Reset contact selection
      setVatOverrideAccepted(false);  // Reset VAT override
      previousAvailableIdsRef.current = new Set();
      setShowAddAccount(false);
      // Auto-select default bank account
      const defaultAccount = bankAccounts.find((a: BankAccount) => a.is_default);
      setBankAccountId(defaultAccount?.id || bankAccounts[0]?.id || null);
    }
  }, [isOpen, bankAccounts]);

  // Clear user modifications when affaireId changes (different dossier)
  useEffect(() => {
    if (affaireId !== lastAffaireIdRef.current) {
      lastAffaireIdRef.current = affaireId;
      setUserModifiedQuantities(new Map());
      setUnavailableProducts(new Map());
      setQuantitiesAdjusted(false);
      previousAvailableIdsRef.current = new Set();
    }
  }, [affaireId]);

  // Track unavailable products and adjust user modifications when products change
  // RACE CONDITION FIX: This effect no longer sets productQuantities directly.
  // Instead, it only:
  // 1. Tracks products that became unavailable (for warning display)
  // 2. Adjusts userModifiedQuantities when remaining quantity decreases below user's selection
  // The actual productQuantities is computed via useMemo, preserving user modifications
  useEffect(() => {
    const currentAvailableIds = new Set(availableProducts.map(p => p.id));
    const previousAvailableIds = previousAvailableIdsRef.current;

    // Only check for unavailable products if we have a meaningful previous state
    // and the sets are actually different (not just a React Query refetch with same data)
    const setsAreDifferent = previousAvailableIds.size > 0 && (
      previousAvailableIds.size !== currentAvailableIds.size ||
      [...previousAvailableIds].some(id => !currentAvailableIds.has(id))
    );

    // Detect products that became unavailable (were in previous set but not in current)
    if (setsAreDifferent) {
      const currentQuantities = productQuantitiesRef.current;

      setUnavailableProducts(prevUnavailable => {
        const newUnavailable = new Map(prevUnavailable);
        let hasNewUnavailable = false;

        previousAvailableIds.forEach(prevId => {
          if (!currentAvailableIds.has(prevId)) {
            // Product became unavailable - check if it was selected
            const selectedQty = currentQuantities.get(prevId);
            if (selectedQty && selectedQty > 0) {
              // Find product info from enrichedProducts or fullyInvoiced list
              const fullyInvoicedProduct = enrichedProducts.find(p => p.id === prevId && p.fully_invoiced);
              const removedProduct = enrichedProducts.find(p => p.id === prevId);

              if (fullyInvoicedProduct) {
                newUnavailable.set(prevId, {
                  description: fullyInvoicedProduct.description,
                  reason: 'fully_invoiced'
                });
                hasNewUnavailable = true;
              } else if (!removedProduct) {
                // Product was removed entirely (not in enrichedProducts anymore)
                newUnavailable.set(prevId, {
                  description: `Produit #${prevId}`,
                  reason: 'removed'
                });
                hasNewUnavailable = true;
              }
            }
          }
        });

        return hasNewUnavailable ? newUnavailable : prevUnavailable;
      });

      // Clean up user modifications for products that are no longer available
      setUserModifiedQuantities(prev => {
        let hasChanges = false;
        const updated = new Map(prev);
        prev.forEach((_, productId) => {
          if (!currentAvailableIds.has(productId)) {
            updated.delete(productId);
            hasChanges = true;
          }
        });
        return hasChanges ? updated : prev;
      });
    }

    // Check if any user modifications exceed the new remaining quantity
    // This handles partial invoicing by another user while modal is open
    let hasAdjustments = false;
    setUserModifiedQuantities(prev => {
      const updated = new Map(prev);
      let hasChanges = false;

      availableProducts.forEach(p => {
        const userQty = prev.get(p.id);
        if (userQty !== undefined) {
          const newRemaining = p.remaining_quantity ?? p.quantity;
          if (userQty > newRemaining) {
            updated.set(p.id, newRemaining);
            hasChanges = true;
            hasAdjustments = true;
          }
        }
      });

      return hasChanges ? updated : prev;
    });

    if (hasAdjustments) {
      setQuantitiesAdjusted(true);
    }

    // Update ref for next comparison
    previousAvailableIdsRef.current = currentAvailableIds;
  }, [availableProducts, enrichedProducts]);

  // VAT default: intra-EU B2B (EU ≠ BE + valid VAT) → 0% autoliquidation; everything else (incl. Switzerland) → 21%.
  // The operator can override this by hand on any foreign customer.
  const billingCountry = (billingAddressEdit.countryCode || 'BE').toUpperCase();
  const isForeignCustomer = billingCountry !== 'BE';
  const isEuCustomer = useMemo(() => {
    const EU = new Set(['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','EL','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE']);
    return EU.has(billingCountry);
  }, [billingCountry]);
  const autoZeroVat = isForeignCustomer && isEuCustomer && isValidVatNumber(billingContact?.vat_number);
  // Operator override wins; otherwise the default above.
  const reverseChargeActive = zeroVatOverride !== null ? zeroVatOverride : autoZeroVat;

  // Calculate selected products summary with discount
  // IMPORTANT: Discount TVA must be calculated per-product using each product's VAT rate
  // to comply with Belgian VAT regulations (products can have 6%, 12%, or 21% VAT)
  // TVAC PRECISION: When unit_price_tvac is available (from tarif import), use it
  // as source of truth and derive TVA as TVAC - HTVA to avoid rounding errors.
  const computedSummary = useMemo(() => {
    if (enrichedProducts.length === 0) return null;

    let totalHtva = 0;
    let totalTva = 0;
    let totalTvac = 0;
    let selectedCount = 0;

    // First pass: calculate totals and collect product data for proportional discount
    const productTotals: Array<{ lineHtva: number; lineTvac: number; vatRate: number }> = [];
    enrichedProducts.forEach(p => {
      const qty = productQuantities.get(p.id);
      if (qty && qty > 0) {
        const lineHtva = qty * Number(p.unit_price_htva);
        const vatRate = Number(p.tva_rate) / 100;
        let lineTva: number;
        let lineTvac: number;

        // Use TVAC if available for exact calculations (no rounding errors)
        if (p.unit_price_tvac) {
          lineTvac = qty * Number(p.unit_price_tvac);
          lineTva = lineTvac - lineHtva;  // Derive TVA for exactness
        } else {
          // Fallback for legacy data without stored TVAC
          lineTva = lineHtva * vatRate;
          lineTvac = lineHtva + lineTva;
        }

        totalHtva += lineHtva;
        totalTva += lineTva;
        totalTvac += lineTvac;
        selectedCount++;
        productTotals.push({ lineHtva, lineTvac, vatRate });
      }
    });

    // Apply discount
    let discountAmount = 0;
    if (discountValue > 0) {
      if (discountType === 'percentage') {
        discountAmount = totalHtva * (discountValue / 100);
      } else {
        discountAmount = discountValue;
      }
    }

    const finalHtva = totalHtva - discountAmount;

    // Calculate discount TVA correctly: allocate discount proportionally to each product
    // and apply each product's VAT rate to its portion of the discount
    let discountTva = 0;
    if (discountAmount > 0 && totalHtva > 0) {
      productTotals.forEach(({ lineHtva, vatRate }) => {
        // Proportional allocation of discount based on product's share of total HTVA
        const proportion = lineHtva / totalHtva;
        const productDiscountHtva = discountAmount * proportion;
        // Apply the product's specific VAT rate to its portion of the discount
        discountTva += productDiscountHtva * vatRate;
      });
    }

    const finalTva = totalTva - discountTva;
    const finalTvac = finalHtva + finalTva;

    // Reverse charge → invoice is 0% VAT
    if (reverseChargeActive) {
      return {
        lineCount: selectedCount,
        totalHtva,
        totalTva: 0,
        totalTvac: totalHtva,
        discountAmount,
        finalHtva,
        finalTva: 0,
        finalTvac: finalHtva
      };
    }

    return {
      lineCount: selectedCount,
      totalHtva,
      totalTva,
      totalTvac,
      discountAmount,
      finalHtva,
      finalTva,
      finalTvac
    };
  }, [enrichedProducts, productQuantities, discountType, discountValue, reverseChargeActive]);

  // Check VAT when billing contact changes
  // Note: VAT validation is automatically throttled to 1 request/second
  // to comply with VIES API rate limits.
  // Uses ref pattern to avoid infinite loop from mutation function changing on each render.
  // IMPORTANT: When contact changes to one without VAT number, clear the old validation result
  // to avoid using VIES data from a different contact.
  useEffect(() => {
    if (!isOpen) return;

    // Only validate if VAT number is valid (not "NA", "N/A", empty, etc.)
    if (isValidVatNumber(billingContact?.vat_number)) {
      // Normalize VAT number: add country prefix if missing
      // Use pays field if it's a valid 2-letter code, otherwise default to BE
      let vatNumber = billingContact!.vat_number!.replace(/[.\s-]/g, '').toUpperCase();

      // If already has 2-letter prefix, use as-is
      if (!/^[A-Z]{2}/.test(vatNumber)) {
        // Determine prefix from pays field or default to BE
        const pays = billingContact?.pays;
        const prefix = (pays && /^[A-Z]{2}$/i.test(pays)) ? pays.toUpperCase() : 'BE';

        if (prefix === 'BE') {
          // Belgian-specific: handle 9/10 digit numbers
          if (/^\d{10}$/.test(vatNumber)) {
            vatNumber = 'BE' + vatNumber;
          } else if (/^\d{9}$/.test(vatNumber)) {
            vatNumber = 'BE0' + vatNumber;
          }
        } else if (/^\d+$/.test(vatNumber)) {
          // For other countries, prepend the prefix to digits-only VAT
          vatNumber = prefix + vatNumber;
        }
      }

      validateVatMutateRef.current(
        {
          vatNumber,
          storedName: billingContact!.name,
          storedAddress: billingContact?.address ?? undefined
        },
        {
          onSuccess: (result) => {
            setVatValidationResult(result);
          }
        }
      );
    } else {
      // Contact has no valid VAT number (NA, N/A, empty, etc.) - clear any previous validation result
      // This prevents using VIES data from a different contact
      setVatValidationResult(null);
    }
  }, [billingContact?.contact_id, billingContact?.vat_number, billingContact?.name, billingContact?.address, billingContact?.pays, isOpen]);

  const handleGenerate = async () => {
    // Validate quantities against current availability before submitting
    const validationErrors: string[] = [];
    const adjustedSelections: ProductSelection[] = [];

    productQuantities.forEach((quantity, productId) => {
      if (quantity > 0) {
        const product = productMap.get(productId);
        if (!product) {
          validationErrors.push(`Le produit #${productId} n'est plus disponible.`);
          return;
        }
        if (product.fully_invoiced) {
          validationErrors.push(`"${product.description}" est deja entierement facture.`);
          return;
        }
        const maxRemaining = product.remaining_quantity ?? product.quantity;
        if (quantity > maxRemaining) {
          validationErrors.push(`"${product.description}": quantite demandee (${quantity}) depasse la quantite disponible (${maxRemaining}).`);
          return;
        }
        adjustedSelections.push({ productId, quantity });
      }
    });

    // If there are validation errors, show them and don't proceed
    if (validationErrors.length > 0) {
      setStep('error');
      setErrorMessage(
        'Erreur de validation:\n' + validationErrors.join('\n') +
        '\n\nVeuillez corriger les quantites et reessayer.'
      );
      return;
    }

    // Check if there are any products to invoice
    if (adjustedSelections.length === 0) {
      setStep('error');
      setErrorMessage('Aucun produit selectionne. Veuillez selectionner au moins un produit a facturer.');
      return;
    }

    setStep('generating');
    try {
      // Build discount object if a discount is applied
      const discount: Discount | undefined = discountValue > 0 ? {
        code: 'MANUAL',
        type: discountType,
        value: discountValue,
        description: 'Remise manuelle'
      } : undefined;

      // Include VIES official data if available (for professional customers)
      // This data will be used for the billing name and address on the invoice
      const viesOfficialData = vatValidationResult?.isValid && vatValidationResult.officialData
        ? {
            name: vatValidationResult.officialData.name,
            address: vatValidationResult.officialData.address
          }
        : undefined;

      const invoice = await generateMutation.mutateAsync({
        affaireId,
        language: selectedLanguage,
        billingContactId: billingContacts.length > 1 ? billingContact?.contact_id : undefined,
        productSelections: adjustedSelections.length > 0 ? adjustedSelections : undefined,
        discount,
        viesOfficialData,
        bankAccountId: bankAccountId || undefined,
        billingAddressOverride: {
          address: billingAddressEdit.address.trim() || undefined,
          postalCode: billingAddressEdit.postalCode.trim() || undefined,
          city: billingAddressEdit.city.trim() || undefined,
          countryCode: billingAddressEdit.countryCode || undefined,
        },
        reverseCharge: reverseChargeActive
      });
      setGeneratedInvoice(invoice as Invoice);
      setStep('preview');
    } catch (error: unknown) {
      setStep('error');
      setErrorMessage(extractApiErrorMessage(error, 'Une erreur est survenue'));
    }
  };

  // Handle validate (send to Falco)
  const handleValidate = async () => {
    if (!generatedInvoice) return;

    setIsValidating(true);
    setStep('finalizing');
    try {
      // Send email based on user choice
      const result = await validateMutation.mutateAsync({
        id: generatedInvoice.id,
        currentStatus: generatedInvoice.status,
        sendEmail: sendEmailOnValidate
      });

      // Update generatedInvoice with validated data (invoice_number, pdf_path, status)
      // and store email result for success display
      // Backend returns { success: true, data: { invoice, emailSent, emailRecipient, emailError, ... } }
      const responseData = (result as { data?: { invoice?: Invoice; emailSent?: boolean; emailRecipient?: string | null; emailError?: string | null } })?.data;
      const validatedInvoice = responseData?.invoice;
      // Use snake_case to match Invoice type definition
      const email_sent = responseData?.emailSent ?? false;
      const email_recipient = responseData?.emailRecipient ?? null;
      const email_error = responseData?.emailError ?? null;

      if (validatedInvoice) {
        // Use the returned validated invoice and merge email data
        setGeneratedInvoice({
          ...validatedInvoice,
          email_sent,
          email_recipient,
          email_error
        } as Invoice);
      } else {
        // Fallback: only update email data if no invoice returned
        setGeneratedInvoice(prev => prev ? {
          ...prev,
          email_sent,
          email_recipient,
          email_error
        } as Invoice : null);
      }

      setStep('success');
      // Note: Don't call onSuccess here - let user see the success step first
      // onSuccess will be called when user closes the modal
    } catch (error: unknown) {
      setStep('error');
      setErrorMessage(extractApiErrorMessage(error, 'Une erreur est survenue lors de l\'envoi'));
    } finally {
      setIsValidating(false);
    }
  };

  // Handle PDF download
  const handleDownloadPdf = async () => {
    if (!generatedInvoice) return;

    setIsDownloading(true);
    try {
      const blob = await downloadInvoicePdf(generatedInvoice.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = generatedInvoice.invoice_number
        ? `facture_${generatedInvoice.invoice_number.replace(/\//g, '-')}.pdf`
        : `facture_brouillon_${generatedInvoice.id}.pdf`;
      a.click();
      // Revoke after download should have completed (30 seconds)
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (error) {
      console.error('Download failed:', error);
      addNotification({
        type: 'error',
        title: 'Erreur de téléchargement',
        message: 'Impossible de télécharger le PDF. Veuillez réessayer.'
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Create a Map for O(1) product lookups
  const productMap = useMemo(() => {
    return new Map(enrichedProducts.map(p => [p.id, p]));
  }, [enrichedProducts]);

  // Toggle product selection (sets to remaining qty or 0)
  // RACE CONDITION FIX: Updates userModifiedQuantities, not productQuantities directly
  const toggleProduct = (productId: number) => {
    const product = productMap.get(productId);
    const currentQty = productQuantities.get(productId) || 0;

    if (currentQty > 0) {
      // Deselect - explicitly set to 0
      setUserModifiedQuantities(prev => {
        const newMap = new Map(prev);
        newMap.set(productId, 0);
        return newMap;
      });
    } else {
      // Select with remaining quantity - remove from userModified to use default
      // Or explicitly set if product exists
      const remaining = product?.remaining_quantity ?? product?.quantity ?? 1;
      setUserModifiedQuantities(prev => {
        const newMap = new Map(prev);
        newMap.set(productId, remaining);
        return newMap;
      });
    }
  };

  // Update product quantity
  // RACE CONDITION FIX: Updates userModifiedQuantities, not productQuantities directly
  const updateProductQuantity = (productId: number, quantity: number) => {
    const product = productMap.get(productId);
    const maxQty = product?.remaining_quantity ?? product?.quantity ?? 1;
    // Clamp between 0 and max
    const clampedQty = Math.max(0, Math.min(quantity, maxQty));

    setUserModifiedQuantities(prev => {
      const newMap = new Map(prev);
      newMap.set(productId, clampedQty);
      return newMap;
    });
  };

  // Select/deselect all products
  // RACE CONDITION FIX: Updates userModifiedQuantities, not productQuantities directly
  const toggleAllProducts = () => {
    if (availableProducts.length > 0) {
      // Check if all are currently selected (qty > 0)
      const allSelected = availableProducts.every(p => (productQuantities.get(p.id) || 0) > 0);

      if (allSelected) {
        // Deselect all - explicitly set all to 0
        setUserModifiedQuantities(prev => {
          const newMap = new Map(prev);
          availableProducts.forEach(p => newMap.set(p.id, 0));
          return newMap;
        });
      } else {
        // Select all with remaining quantities
        setUserModifiedQuantities(prev => {
          const newMap = new Map(prev);
          availableProducts.forEach(p => {
            const remaining = p.remaining_quantity ?? p.quantity;
            newMap.set(p.id, remaining);
          });
          return newMap;
        });
      }
    }
  };

  // Apply split by parts (e.g., 3 parts = 1/3 of each product)
  // IMPORTANT: Split is based on ORIGINAL quantity, not remaining quantity
  // This ensures each invoice has the same amount (e.g., 3 invoices of 1/3 each)
  // RACE CONDITION FIX: Updates userModifiedQuantities, not productQuantities directly
  const applySplitByParts = (totalParts: number, currentPart: number = 1) => {
    if (availableProducts.length > 0 && totalParts > 0 && currentPart > 0 && currentPart <= totalParts) {
      setUserModifiedQuantities(() => {
        const newMap = new Map<number, number>();
        availableProducts.forEach(p => {
          // Use original quantity for split calculation (not remaining)
          const original = p.original_quantity ?? p.quantity;
          const remaining = p.remaining_quantity ?? p.quantity;
          // Calculate 1/totalParts of ORIGINAL quantity, round to 2 decimal places
          const partQty = Math.round((original / totalParts) * 100) / 100;
          // Cap at remaining quantity (can't invoice more than what's left)
          const qty = Math.min(partQty, remaining);
          newMap.set(p.id, qty);
        });
        return newMap;
      });
    }
  };

  // Determine if VAT validation blocks generation
  // VAT validation is required if a valid VAT number is present, but we allow generation if:
  // - No VAT number or invalid VAT (NA, N/A, etc.) - B2C customer
  // - VAT is valid
  // - RATE_LIMIT error (service unavailable, allow user to proceed)
  // - User has accepted to override the invalid VAT warning
  const hasVatNumber = isValidVatNumber(billingContact?.vat_number);
  const isVatValidating = validateVatMutation.isPending;
  const isVatInvalid = hasVatNumber &&
    vatValidationResult &&
    !vatValidationResult.isValid &&
    vatValidationResult.errorType !== 'RATE_LIMIT' &&
    vatValidationResult.errorType !== 'UNSUPPORTED_COUNTRY';
  const isVatValidationBlocking = isVatInvalid && !vatOverrideAccepted;

  // Check if all products are unavailable (either none available or all fully invoiced)
  const allProductsUnavailable = availableProducts.length === 0 && enrichedProducts.length > 0;

  // Check if no billing contact is available (either error fetching or empty array)
  const noBillingContact = !billingContactsLoading && (billingContactsError || billingContacts.length === 0);

  const canGenerate = eligibility?.eligible &&
    !allProductsUnavailable &&
    !noBillingContact &&
    computedSummary &&
    computedSummary.lineCount > 0 &&
    computedSummary.finalHtva > 0 &&
    !isVatValidating &&
    !isVatValidationBlocking;

  return (
    <ModalErrorBoundary>
    <>
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        onClose={() => {
          // Don't close the main modal while PDF preview is open
          if (!showPdfPreview) {
            onClose();
          }
        }}
        className="relative z-50"
        // Focus trap is automatically enabled by HeadlessUI Dialog
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl max-h-[90vh] transform rounded-2xl bg-white shadow-xl transition-all flex flex-col">
                {/* Header - Focus trap is automatically handled by HeadlessUI Dialog */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <DocumentTextIcon className="h-6 w-6 text-primary" />
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        {step === 'check' && 'Generer une facture'}
                        {step === 'generating' && 'Generation en cours...'}
                        {step === 'preview' && 'Verifier la facture'}
                        {step === 'finalizing' && 'Envoi en cours...'}
                        {step === 'success' && 'Facture envoyee'}
                        {step === 'error' && 'Erreur'}
                      </Dialog.Title>
                      <p className="text-sm text-gray-500">
                        {step === 'preview' && generatedInvoice ? (
                          generatedInvoice.invoice_number || 'Brouillon'
                        ) : (
                          <>
                            Dossier {dossierNumber || affaireId}
                            {billingContact && (
                              <span className="ml-2">
                                • <span className="font-medium text-gray-700">{billingContact.name}</span>
                                {billingContact.vat_number && (
                                  <span className="ml-1 font-mono text-xs text-gray-400">({billingContact.vat_number})</span>
                                )}
                              </span>
                            )}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {step === 'check' && billingContact && (
                      <button
                        type="button"
                        onClick={() => setShowAddressEdit(v => !v)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                          showAddressEdit
                            ? 'bg-primary/10 border-primary/30 text-primary'
                            : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                        }`}
                        title="Modifier l'adresse de facturation"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                        Adresse
                      </button>
                    )}
                    <button
                      onClick={onClose}
                      className="facturation-btn-modal-close"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Content - scrollable */}
                <div className="p-6 overflow-y-auto flex-1">
                  {/* Loading state */}
                  {isCheckingEligibility && (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                      <p className="mt-4 text-gray-600">Verification en cours...</p>
                    </div>
                  )}

                  {/* Eligibility check failed - only show in check step */}
                  {!isCheckingEligibility && !eligibility?.eligible && step === 'check' && (
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <ExclamationTriangleIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
                        <div>
                          <h3 className="font-medium text-red-900">Facturation impossible</h3>
                          <p className="text-sm text-red-700 mt-1">
                            {eligibility?.reason || 'Cette affaire ne peut pas etre facturee.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* No billing contact error - only show in check step when eligible */}
                  {!isCheckingEligibility && !billingContactsLoading && eligibility?.eligible && noBillingContact && step === 'check' && (
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <ExclamationTriangleIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
                        <div>
                          <h3 className="font-medium text-red-900">Contact de facturation manquant</h3>
                          <p className="text-sm text-red-700 mt-1">
                            Aucun contact de facturation trouve pour cette affaire.
                          </p>
                          <p className="text-sm text-red-700 mt-2">
                            Veuillez ajouter un contact avec le role <span className="font-semibold">"FACTURE"</span> dans FileMaker avant de generer une facture.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Ready to generate */}
                  {!isCheckingEligibility && eligibility?.eligible && step === 'check' && (
                    <div className="space-y-6">
                      {/* Billing contact selector (when multiple contacts exist) */}
                      {billingContacts.length > 1 && (
                        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                          <div className="flex items-start gap-3">
                            <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 flex-shrink-0" />
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-amber-900">Plusieurs contacts de facturation</h4>
                              <p className="text-sm text-amber-700 mt-1 mb-3">
                                Ce dossier a {billingContacts.length} contacts avec le role FACTURE. Selectionnez celui a utiliser pour cette facture.
                              </p>
                              <select
                                value={selectedContactId || billingContact?.contact_id || ''}
                                onChange={(e) => setSelectedContactId(e.target.value)}
                                className="w-full px-3 py-2 border border-amber-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                aria-label="Selectionner le contact de facturation"
                              >
                                {billingContacts.map((contact) => (
                                  <option key={contact.contact_id} value={contact.contact_id}>
                                    {contact.name}
                                    {contact.vat_number && ` (${contact.vat_number})`}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Bank account selector */}
                      {bankAccounts.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Compte bancaire</h4>
                          <select
                            value={bankAccountId || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '__add__') {
                                setShowAddAccount(true);
                              } else {
                                setBankAccountId(Number(val));
                                setShowAddAccount(false);
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-[#ef5343] focus:border-[#ef5343]"
                          >
                            {bankAccounts.map((account: BankAccount) => (
                              <option key={account.id} value={account.id}>
                                {account.label} — {account.iban.replace(/(.{4})/g, '$1 ').trim()}
                              </option>
                            ))}
                            <option value="__add__">+ Ajouter un compte</option>
                          </select>
                          {showAddAccount && (
                            <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200 space-y-2">
                              <input
                                type="text"
                                placeholder="Libelle (ex: BNP Secondaire)"
                                value={newAccountLabel}
                                onChange={(e) => setNewAccountLabel(e.target.value)}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm"
                              />
                              <input
                                type="text"
                                placeholder="IBAN (ex: BE72652837171316)"
                                value={newAccountIban}
                                onChange={(e) => setNewAccountIban(e.target.value)}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm font-mono"
                              />
                              <input
                                type="text"
                                placeholder="BIC (ex: BBRUBEBB)"
                                value={newAccountBic}
                                onChange={(e) => setNewAccountBic(e.target.value)}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm font-mono"
                              />
                              <div className="flex gap-2 pt-1">
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (!newAccountLabel || !newAccountIban || !newAccountBic) return;
                                    try {
                                      const created = await addBankAccountMutation.mutateAsync({
                                        label: newAccountLabel,
                                        iban: newAccountIban,
                                        bic: newAccountBic,
                                      });
                                      setBankAccountId(created.id);
                                      setShowAddAccount(false);
                                      setNewAccountLabel('');
                                      setNewAccountIban('');
                                      setNewAccountBic('');
                                    } catch {
                                      // mutation error handled by React Query
                                    }
                                  }}
                                  disabled={!newAccountLabel || !newAccountIban || !newAccountBic || addBankAccountMutation.isPending}
                                  className="px-3 py-1.5 text-xs font-medium bg-[#ef5343] text-white rounded hover:bg-[#d94838] disabled:opacity-50"
                                >
                                  {addBankAccountMutation.isPending ? 'Ajout...' : 'Ajouter'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowAddAccount(false);
                                    // Revert to default account selection
                                    const defaultAccount = bankAccounts.find((a: BankAccount) => a.is_default);
                                    setBankAccountId(defaultAccount?.id || bankAccounts[0]?.id || null);
                                  }}
                                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                                >
                                  Annuler
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Editable billing address (hidden by default; toggled via the "Adresse" button in the header) */}
                      {billingContact && showAddressEdit && (
                        <div className="bg-gray-50 rounded-lg p-4 border border-primary/20">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Adresse de facturation</h4>
                          <textarea
                            value={billingAddressEdit.address}
                            onChange={(e) => setBillingAddressEdit({ ...billingAddressEdit, address: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-[#ef5343] focus:border-[#ef5343]"
                            rows={2}
                            placeholder="Rue, numero"
                          />
                          <div className="grid grid-cols-3 gap-3 mt-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Code postal</label>
                              <input
                                type="text"
                                value={billingAddressEdit.postalCode}
                                onChange={(e) => setBillingAddressEdit({ ...billingAddressEdit, postalCode: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-[#ef5343] focus:border-[#ef5343]"
                                placeholder="1000"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Ville</label>
                              <input
                                type="text"
                                value={billingAddressEdit.city}
                                onChange={(e) => setBillingAddressEdit({ ...billingAddressEdit, city: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-[#ef5343] focus:border-[#ef5343]"
                                placeholder="Bruxelles"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Pays</label>
                              <select
                                value={billingAddressEdit.countryCode}
                                onChange={(e) => setBillingAddressEdit({ ...billingAddressEdit, countryCode: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-[#ef5343] focus:border-[#ef5343]"
                              >
                                {COUNTRY_OPTIONS.map((c) => (
                                  <option key={c.code} value={c.code}>{c.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Product selection with partial invoicing */}
                      {enrichedProducts.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-gray-700">Produits a facturer</h4>
                            <div className="flex items-center gap-3">
                              {/* Split by parts */}
                              {availableProducts.length > 0 && (
                                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg transition-colors ${
                                  splitParts && splitParts > 1
                                    ? 'bg-primary/10 ring-1 ring-primary/30'
                                    : 'bg-transparent'
                                }`}>
                                  <span className={`text-xs ${splitParts && splitParts > 1 ? 'text-primary font-medium' : 'text-gray-500'}`}>
                                    Diviser en
                                  </span>
                                  <input
                                    type="number"
                                    min="1"
                                    max="10"
                                    value={splitParts ?? ''}
                                    placeholder="-"
                                    className={`w-10 text-xs text-center border rounded px-1 py-1 focus:ring-1 focus:ring-primary focus:border-primary transition-colors ${
                                      splitParts && splitParts > 1
                                        ? 'border-primary bg-white font-medium text-primary'
                                        : 'border-gray-300 bg-white text-gray-700'
                                    }`}
                                    aria-label="Nombre de parts pour diviser les produits"
                                    onChange={(e) => {
                                      const value = parseInt(e.target.value);
                                      if (e.target.value === '' || isNaN(value)) {
                                        setSplitParts(null);
                                      } else if (value >= 1 && value <= 10) {
                                        setSplitParts(value);
                                        if (value > 1) {
                                          applySplitByParts(value);
                                        }
                                      }
                                    }}
                                  />
                                  <span className={`text-xs ${splitParts && splitParts > 1 ? 'text-primary font-medium' : 'text-gray-500'}`}>
                                    parts
                                  </span>
                                  {splitParts && splitParts > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSplitParts(null);
                                        // Reset to full remaining quantities - clear user modifications
                                        // so useMemo uses default (remaining quantity)
                                        setUserModifiedQuantities(new Map());
                                      }}
                                      className="ml-1 text-primary hover:text-primary/80 text-xs"
                                      title="Annuler la division"
                                    >
                                      ✕
                                    </button>
                                  )}
                                </div>
                              )}
                              {availableProducts.length > 0 && (
                                <button
                                  type="button"
                                  onClick={toggleAllProducts}
                                  className="text-xs text-primary hover:underline"
                                >
                                  {availableProducts.every(p => (productQuantities.get(p.id) || 0) > 0) ? 'Aucun' : 'Tout'}
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Warning banner for unavailable products */}
                          {unavailableProducts.size > 0 && (
                            <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <div className="flex items-start gap-2">
                                <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-amber-800">
                                    Produits retires de la selection
                                  </p>
                                  <ul className="mt-1 text-xs text-amber-700 space-y-0.5">
                                    {Array.from(unavailableProducts.entries()).map(([id, info]) => (
                                      <li key={id}>
                                        <span className="font-medium">{info.description}</span>
                                        {info.reason === 'fully_invoiced'
                                          ? ' - deja entierement facture'
                                          : ' - retire du dossier'}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setUnavailableProducts(new Map())}
                                  className="ml-auto text-amber-600 hover:text-amber-800"
                                  aria-label="Fermer l'avertissement"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Warning banner for adjusted quantities */}
                          {quantitiesAdjusted && (
                            <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <div className="flex items-start gap-2">
                                <InformationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-sm text-blue-800">
                                    Certaines quantites ont ete ajustees car la quantite disponible a change.
                                    Veuillez verifier les selections avant de continuer.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setQuantitiesAdjusted(false)}
                                  className="ml-auto text-blue-600 hover:text-blue-800"
                                  aria-label="Fermer l'avertissement"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Warning when no products are available */}
                          {availableProducts.length === 0 && enrichedProducts.length > 0 && (
                            <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3">
                              <div className="flex items-start gap-2">
                                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-red-800">
                                    Tous les produits ont deja ete factures
                                  </p>
                                  <p className="mt-1 text-xs text-red-700">
                                    Aucun produit n'est disponible pour facturation. Tous les produits de ce dossier ont deja ete entierement factures.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="space-y-2">
                            {/* Available products */}
                            {availableProducts.map((product) => {
                              const selectedQty = productQuantities.get(product.id) || 0;
                              const isSelected = selectedQty > 0;
                              const maxQty = product.remaining_quantity ?? product.quantity;
                              const lineTotal = selectedQty * Number(product.unit_price_htva);
                              const hasInvoiced = (product.invoiced_percentage ?? 0) > 0;

                              return (
                                <div
                                  key={product.id}
                                  className={`p-3 rounded-lg transition-colors ${
                                    isSelected ? 'bg-primary/5 border border-primary/20' : 'bg-white border border-gray-200'
                                  }`}
                                >
                                  {/* Single line layout: checkbox | description | qty controls | price */}
                                  <div className="flex items-center gap-3">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => toggleProduct(product.id)}
                                      className="rounded border-gray-300 text-primary focus:ring-primary flex-shrink-0"
                                      aria-label={`Sélectionner ${product.description}`}
                                    />
                                    {/* Product info */}
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm truncate ${isSelected ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                                        {product.description}
                                      </p>
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-xs text-gray-400">
                                          {formatCurrency(Number(product.unit_price_htva))} / unite
                                        </span>
                                        <span className="text-xs text-gray-400">
                                          (dispo: {maxQty}/{product.original_quantity ?? product.quantity})
                                        </span>
                                        {/* Invoice badges for partial invoicing */}
                                        {hasInvoiced && product.invoices && product.invoices.length > 0 && (
                                          <div className="flex items-center gap-1">
                                            {product.invoices.map((inv, idx) => (
                                              <span
                                                key={`${product.id}-${inv.invoice_number || 'draft'}-${idx}`}
                                                className={`text-xs px-1.5 py-0.5 rounded ${
                                                  inv.status === 'PAID'
                                                    ? 'bg-green-100 text-green-700'
                                                    : inv.status === 'SENT'
                                                    ? 'bg-blue-100 text-blue-700'
                                                    : 'bg-amber-100 text-amber-700'
                                                }`}
                                                title={`${formatCurrency(inv.amount_htva)} HTVA - ${inv.quantity} unites`}
                                              >
                                                {inv.invoice_number ? inv.invoice_number.split('/').pop() : 'Brouillon'}{inv.status === 'PAID' && ' ✓'}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {/* Quantity controls - always visible */}
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <div className="flex items-center border border-gray-300 rounded-lg">
                                        <button
                                          type="button"
                                          onClick={() => updateProductQuantity(product.id, Math.max(0, selectedQty - 1))}
                                          disabled={selectedQty <= 0}
                                          className="p-1 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed rounded-l-lg"
                                        >
                                          <MinusIcon className="h-4 w-4" />
                                        </button>
                                        <input
                                          type="number"
                                          value={selectedQty}
                                          onChange={(e) => updateProductQuantity(product.id, parseFloat(e.target.value) || 0)}
                                          min={0}
                                          max={maxQty}
                                          step={1}
                                          aria-label={`Quantité pour ${product.description}`}
                                          aria-valuemin={0}
                                          aria-valuemax={maxQty}
                                          aria-valuenow={selectedQty}
                                          className="w-16 text-center text-sm border-x border-gray-300 py-1 focus:outline-none focus:ring-1 focus:ring-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => updateProductQuantity(product.id, Math.min(maxQty, selectedQty + 1))}
                                          disabled={selectedQty >= maxQty}
                                          className="p-1 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed rounded-r-lg"
                                        >
                                          <PlusIcon className="h-4 w-4" />
                                        </button>
                                      </div>
                                      <span className="text-xs text-gray-400 w-12">/ {maxQty}</span>
                                    </div>
                                    {/* Line total */}
                                    <div className="text-sm font-medium text-primary w-24 text-right flex-shrink-0">
                                      {formatCurrency(lineTotal)}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}

                            {/* Fully invoiced products (grayed out, same card style) */}
                            {fullyInvoicedProducts.map((product) => (
                              <div
                                key={product.id}
                                className="p-3 rounded-lg bg-gray-100 border border-gray-200"
                              >
                                <div className="flex items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={false}
                                    disabled
                                    className="rounded border-gray-300 text-gray-300 flex-shrink-0 cursor-not-allowed"
                                  />
                                  {/* Product info */}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-400 truncate">
                                      {product.description}
                                    </p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-xs text-gray-400">
                                        {formatCurrency(Number(product.unit_price_htva))} / unite
                                      </span>
                                      {/* Invoice badges */}
                                      {product.invoices && product.invoices.length > 0 && (
                                        <div className="flex items-center gap-1">
                                          {product.invoices.map((inv, idx) => (
                                            <span
                                              key={`${product.id}-${inv.invoice_number || 'draft'}-${idx}`}
                                              className={`text-xs px-1.5 py-0.5 rounded ${
                                                inv.status === 'PAID'
                                                  ? 'bg-green-100 text-green-700'
                                                  : inv.status === 'SENT'
                                                  ? 'bg-blue-100 text-blue-700'
                                                  : 'bg-gray-200 text-gray-500'
                                              }`}
                                              title={`${formatCurrency(inv.amount_htva)} HTVA - ${inv.quantity} unites`}
                                            >
                                              {inv.invoice_number ? inv.invoice_number.split('/').pop() : 'Brouillon'}{inv.status === 'PAID' && ' ✓'}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {/* Amount invoiced */}
                                  <div className="text-sm text-gray-400 w-24 text-right flex-shrink-0">
                                    {formatCurrency(product.invoiced_htva ?? 0)}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Foreign customer → VAT control. Default per the rule (EU+VAT → 0% autoliquidation, else 21%); operator can override. */}
                      {isForeignCustomer && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <InformationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                            <div className="text-sm">
                              <p className="font-medium text-blue-900">
                                {isEuCustomer ? `Client UE (${billingCountry})` : `Client hors UE (${billingCountry})`}
                              </p>
                              <p className="text-blue-700 mt-0.5">
                                {autoZeroVat
                                  ? 'Par défaut : autoliquidation (0% TVA, due par le preneur). Décochez pour appliquer 21%.'
                                  : 'Par défaut : TVA belge 21%. Cochez pour facturer à 0% si applicable.'}
                              </p>
                              <label className="flex items-center gap-2 mt-2 text-blue-800">
                                <input
                                  type="checkbox"
                                  checked={reverseChargeActive}
                                  onChange={(e) => setZeroVatOverride(e.target.checked)}
                                  className="rounded border-gray-300 text-primary focus:ring-primary"
                                />
                                <span>Facturer sans TVA (0%){isEuCustomer ? ' — autoliquidation' : ' — hors champ'}</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Billing summary */}
                      {computedSummary && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Produits selectionnes:</span>
                              <span>{computedSummary.lineCount} produit{computedSummary.lineCount > 1 ? 's' : ''}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Total HTVA:</span>
                              <span>{formatCurrency(computedSummary.totalHtva)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">{reverseChargeActive ? `TVA (0% — ${isEuCustomer ? 'autoliquidation' : 'hors champ'}):` : `TVA (${computedSummary.lineCount > 0 ? '21%' : '0%'}):`}</span>
                              <span>{formatCurrency(computedSummary.totalTva)}</span>
                            </div>
                            {/* Discount input and display */}
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-1">
                                <span className="text-gray-600">Remise:</span>
                                <input
                                  type="number"
                                  value={discountValue || ''}
                                  onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                                  min="0"
                                  max={discountType === 'percentage' ? 100 : undefined}
                                  step={discountType === 'percentage' ? 1 : 0.01}
                                  placeholder="0"
                                  className="w-14 px-1 py-0.5 text-xs text-center border border-gray-300 rounded focus:ring-1 focus:ring-primary focus:border-primary"
                                  aria-label="Montant de la remise"
                                  aria-describedby="discount-type-selector"
                                />
                                <select
                                  value={discountType}
                                  onChange={(e) => setDiscountType(e.target.value as 'percentage' | 'fixed')}
                                  className="text-xs border border-gray-300 rounded px-1 py-0.5 focus:ring-1 focus:ring-primary focus:border-primary"
                                >
                                  <option value="percentage">%</option>
                                  <option value="fixed">€</option>
                                </select>
                              </div>
                              <span className={computedSummary.discountAmount > 0 ? 'text-green-600' : 'text-gray-400'}>
                                -{formatCurrency(computedSummary.discountAmount)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center font-semibold border-t pt-2">
                              <span>Total TVAC:</span>
                              <span className="text-primary">{formatCurrency(computedSummary.finalTvac)}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* VAT Validation in progress */}
                      {hasVatNumber && isVatValidating && (
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 flex-shrink-0"></div>
                            <div>
                              <h3 className="font-medium text-blue-900">Validation TVA en cours...</h3>
                              <p className="text-sm text-blue-700 mt-1">
                                Verification du numero de TVA {billingContact?.vat_number} aupres du service VIES.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* VAT Validation error - Invalid VAT */}
                      {isVatInvalid && (
                        <div className={`rounded-lg p-4 ${vatOverrideAccepted ? 'bg-yellow-50' : 'bg-red-50'}`}>
                          <div className="flex items-start gap-3">
                            <ExclamationTriangleIcon className={`h-6 w-6 flex-shrink-0 ${vatOverrideAccepted ? 'text-yellow-600' : 'text-red-600'}`} />
                            <div className="flex-1">
                              <h3 className={`font-medium ${vatOverrideAccepted ? 'text-yellow-900' : 'text-red-900'}`}>
                                Numero de TVA invalide
                              </h3>
                              <p className={`text-sm mt-1 ${vatOverrideAccepted ? 'text-yellow-700' : 'text-red-700'}`}>
                                Le numero de TVA <span className="font-mono">{billingContact?.vat_number}</span> n'est pas enregistre dans le service VIES.
                                {vatValidationResult?.error && (
                                  <span className="block mt-1">{vatValidationResult.error}</span>
                                )}
                              </p>
                              {!vatOverrideAccepted && (
                                <p className="text-xs text-red-600 mt-2">
                                  L'entreprise existe peut-etre mais n'est pas assujettie a la TVA.
                                </p>
                              )}
                              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={vatOverrideAccepted}
                                  onChange={(e) => setVatOverrideAccepted(e.target.checked)}
                                  className="rounded border-gray-300 text-primary focus:ring-primary"
                                />
                                <span className={`text-sm ${vatOverrideAccepted ? 'text-yellow-800 font-medium' : 'text-red-700'}`}>
                                  Je confirme vouloir generer la facture malgre le numero de TVA non valide
                                </span>
                              </label>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* VAT Validation error (rate limit) */}
                      {vatValidationResult && vatValidationResult.errorType === 'RATE_LIMIT' && (
                        <div className="bg-yellow-50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <InformationCircleIcon className="h-6 w-6 text-yellow-600 flex-shrink-0" />
                            <div>
                              <h3 className="font-medium text-yellow-900">Service VIES temporairement indisponible</h3>
                              <p className="text-sm text-yellow-700 mt-1">
                                {vatValidationResult.error}
                              </p>
                              <p className="text-xs text-yellow-600 mt-2">
                                Vous pouvez continuer la generation de facture. La validation TVA peut etre effectuee ulterieurement.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* VAT Validation info - non-EU number (Swiss, etc.), not VIES-verifiable */}
                      {vatValidationResult && vatValidationResult.errorType === 'UNSUPPORTED_COUNTRY' && (
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <InformationCircleIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
                            <div>
                              <h3 className="font-medium text-blue-900">Numéro de TVA hors UE</h3>
                              <p className="text-sm text-blue-700 mt-1">
                                {vatValidationResult.error || 'Numéro de TVA hors UE (Suisse, etc.) — non vérifiable via VIES.'}
                              </p>
                              <p className="text-xs text-blue-600 mt-2">
                                La génération de facture reste possible.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* VAT Validation warning - needs review but valid */}
                      {vatValidationResult && vatValidationResult.isValid && vatValidationResult.needsReview && !vatValidationResult.errorType && (
                        <div className="bg-yellow-50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 flex-shrink-0" />
                            <div>
                              <h3 className="font-medium text-yellow-900">Verification TVA requise</h3>
                              <p className="text-sm text-yellow-700 mt-1">
                                Les donnees officielles VIES different de celles enregistrees.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* VAT Validation success */}
                      {hasVatNumber && vatValidationResult && vatValidationResult.isValid && !vatValidationResult.needsReview && !vatValidationResult.errorType && (
                        <div className="bg-green-50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                            <div>
                              <h3 className="font-medium text-green-900">TVA valide</h3>
                              <p className="text-sm text-green-700 mt-1">
                                Le numero de TVA <span className="font-mono">{billingContact?.vat_number}</span> a ete verifie avec succes.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Peppol status */}
                      {peppolStatus && (
                        <div className={`rounded-lg p-4 ${peppolStatus.peppolRegistered ? 'bg-blue-50' : 'bg-gray-50'}`}>
                          <div className="flex items-start gap-3">
                            <GlobeAltIcon className={`h-6 w-6 ${peppolStatus.peppolRegistered ? 'text-blue-600' : 'text-gray-400'} flex-shrink-0`} />
                            <div>
                              <h3 className={`font-medium ${peppolStatus.peppolRegistered ? 'text-blue-900' : 'text-gray-700'}`}>
                                {peppolStatus.peppolRegistered ? 'Peppol actif' : 'Peppol non disponible'}
                              </h3>
                              <p className={`text-sm mt-1 ${peppolStatus.peppolRegistered ? 'text-blue-700' : 'text-gray-500'}`}>
                                {peppolStatus.peppolRegistered
                                  ? 'La facture sera envoyee via le reseau Peppol.'
                                  : 'La facture sera envoyee par email.'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* VIES Data Comparison - show for professional customers */}
                      {hasVatNumber && billingContact && !isVatValidating && (
                        <VatDataComparison
                          billingContact={{
                            name: billingContact.name,
                            address: billingContact.address,
                            vat_number: billingContact.vat_number,
                            statut_juridique: billingContact.statut_juridique,
                            statut_tva: billingContact.statut_tva,
                            email: billingContact.email
                          }}
                          vatValidationResult={vatValidationResult}
                        />
                      )}

                      {/* Next invoice number */}
                      {nextNumber && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <InformationCircleIcon className="h-4 w-4" />
                          <span>Prochain numero: <span className="font-mono font-medium">{nextNumber}</span></span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Generating state */}
                  {step === 'generating' && (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                      <p className="mt-4 text-gray-600">Generation de la facture et du PDF...</p>
                      <p className="text-sm text-gray-500">Veuillez patienter</p>
                    </div>
                  )}

                  {/* Preview state - PDF generated, ready to validate */}
                  {step === 'preview' && generatedInvoice && (
                    <div className="space-y-6">
                      {/* Draft warning - show when invoice has no number yet */}
                      {!generatedInvoice.invoice_number && (
                        <div className="fact-draft-warning">
                          <p>
                            <strong>Brouillon</strong> - Le numero de facture sera attribue lors de la validation.
                          </p>
                        </div>
                      )}

                      <div className="bg-green-50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                          <div>
                            <h3 className="font-medium text-green-900">
                              {generatedInvoice.invoice_number ? 'Facture generee' : 'Brouillon cree'}
                            </h3>
                            <p className="text-sm text-green-700 mt-1">
                              {generatedInvoice.invoice_number
                                ? <>La facture <span className="font-mono font-medium">{generatedInvoice.invoice_number}</span> a ete generee.</>
                                : <>Le brouillon a ete cree. Cliquez sur "Valider" pour attribuer un numero et envoyer a Falco.</>
                              }
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Invoice summary */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Resume</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Numero:</span>
                            <span className="font-mono font-medium">{generatedInvoice.invoice_number || 'Brouillon'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Client:</span>
                            <span>{generatedInvoice.billing_name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Montant HTVA:</span>
                            <span>{formatCurrency(generatedInvoice.total_htva)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">TVA:</span>
                            <span>{formatCurrency(generatedInvoice.total_tva)}</span>
                          </div>
                          <div className="flex justify-between font-medium text-gray-900 border-t pt-2">
                            <span>Total TVAC:</span>
                            <span className="text-primary">{formatCurrency(generatedInvoice.total_tvac)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Email option */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={sendEmailOnValidate}
                            onChange={(e) => setSendEmailOnValidate(e.target.checked)}
                            disabled={isValidating}
                            className="fact-email-checkbox"
                          />
                          <span className="text-sm text-gray-700">
                            Envoyer la facture par email au client
                          </span>
                        </label>
                        {generatedInvoice.billing_email && (
                          <p className="text-xs text-gray-500 mt-1 ml-7">
                            Email: {generatedInvoice.billing_email}
                          </p>
                        )}
                      </div>

                      {/* PDF preview */}
                      <div className="flex justify-center">
                        {generatedInvoice.pdf_path ? (
                          <button
                            onClick={() => {
                              setIsPdfPreviewMode(false);
                              setShowPdfPreview(true);
                            }}
                            disabled={isValidating}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <EyeIcon className="h-5 w-5" />
                            Voir le PDF
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setIsPdfPreviewMode(true);
                              setShowPdfPreview(true);
                            }}
                            disabled={isValidating}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <EyeIcon className="h-5 w-5" />
                            Previsualiser le brouillon
                          </button>
                        )}
                      </div>

                      {/* Next step info */}
                      <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <PaperAirplaneIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
                          <div>
                            <h3 className="font-medium text-blue-900">Prochaine etape</h3>
                            <p className="text-sm text-blue-700 mt-1">
                              Verifiez le PDF puis cliquez sur "Valider et envoyer" pour transmettre la facture a Falco.
                              {billingContact?.email && (
                                <span className="block mt-1">
                                  La facture sera egalement envoyee par email a{' '}
                                  <span className="font-medium">{billingContact.email}</span>.
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Finalizing state */}
                  {step === 'finalizing' && (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                      <p className="mt-4 text-gray-600">Envoi de la facture a Falco...</p>
                    </div>
                  )}

                  {/* Success state */}
                  {step === 'success' && (
                    <div className="text-center py-8">
                      <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto" />
                      <h3 className="mt-4 text-lg font-semibold text-gray-900">Facture envoyee</h3>
                      <p className="mt-2 text-gray-600">
                        La facture {generatedInvoice?.invoice_number || ''} a ete envoyee a Falco avec succes.
                      </p>

                      {/* Email status */}
                      <div className="fact-success-email-container">
                        {generatedInvoice?.email_sent ? (
                          <div className="fact-success-email-sent">
                            <div className="fact-success-email-sent-header">
                              <EnvelopeIcon className="fact-success-email-sent-icon" />
                              <div>
                                <p className="fact-success-email-sent-title">Email envoye</p>
                                <p className="fact-success-email-sent-message">
                                  La facture a ete envoyee a {generatedInvoice?.email_recipient}
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : generatedInvoice?.email_error ? (
                          <div className="fact-success-email-warning">
                            <div className="fact-success-email-warning-header">
                              <ExclamationTriangleIcon className="fact-success-email-warning-icon" />
                              <div>
                                <p className="fact-success-email-warning-title">Email non envoye</p>
                                <p className="fact-success-email-warning-message">
                                  {generatedInvoice?.email_error}
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>

                      <div className="fact-success-actions">
                        {generatedInvoice?.pdf_path && (
                          <button
                            onClick={handleDownloadPdf}
                            disabled={isDownloading}
                            className="fact-success-download-btn"
                          >
                            {isDownloading ? (
                              <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                Telechargement...
                              </>
                            ) : (
                              <>
                                <ArrowDownTrayIcon className="fact-success-download-btn-icon" />
                                Telecharger le PDF
                              </>
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            onSuccess?.();
                            onClose();
                          }}
                          className="fact-success-close-btn"
                        >
                          Fermer
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Error state */}
                  {step === 'error' && (
                    <div className="text-center py-8">
                      <ExclamationTriangleIcon className="h-12 w-12 text-red-600 mx-auto" />
                      <h3 className="mt-4 text-lg font-semibold text-gray-900">Erreur</h3>
                      <p className="mt-2 text-red-600">{errorMessage}</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
                  {/* Language selection badges */}
                  {step === 'check' && (
                    <div className="flex items-center gap-1">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          type="button"
                          onClick={() => setSelectedLanguage(lang.code)}
                          className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                            selectedLanguage === lang.code
                              ? 'bg-[#ef5343] text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          aria-label={`Langue: ${lang.label}`}
                          aria-pressed={selectedLanguage === lang.code}
                        >
                          {lang.code.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  )}
                  {(step === 'success' || step === 'error' || step === 'generating' || step === 'preview' || step === 'finalizing') && <div />}

                  {/* Action buttons */}
                  <div className="flex gap-3">
                    {step === 'check' && (
                      <>
                        <button
                          onClick={onClose}
                          className="facturation-btn-modal-cancel"
                        >
                          Annuler
                        </button>
                        {/* Show loading button while VAT validation is in progress */}
                        {hasVatNumber && isVatValidating && (
                          <button
                            disabled
                            className="facturation-btn-modal-primary inline-flex items-center gap-2 opacity-50 cursor-not-allowed"
                          >
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Validation TVA...
                          </button>
                        )}
                        {/* Show disabled button with tooltip if VAT is invalid */}
                        {isVatValidationBlocking && (
                          <button
                            disabled
                            className="facturation-btn-modal-primary opacity-50 cursor-not-allowed"
                            title="Le numero de TVA doit etre valide pour generer une facture"
                          >
                            Generer la facture
                          </button>
                        )}
                        {/* Show disabled button with tooltip if no billing contact */}
                        {!isVatValidating && !isVatValidationBlocking && noBillingContact && (
                          <button
                            disabled
                            className="facturation-btn-modal-primary opacity-50 cursor-not-allowed"
                            title="Un contact de facturation avec le role FACTURE est requis"
                          >
                            Generer la facture
                          </button>
                        )}
                        {/* Show enabled button when ready */}
                        {canGenerate && (
                          <button
                            onClick={handleGenerate}
                            className="facturation-btn-modal-primary"
                          >
                            Generer la facture
                          </button>
                        )}
                      </>
                    )}
                    {step === 'preview' && (
                      <>
                        <button
                          onClick={onClose}
                          disabled={isValidating}
                          className="facturation-btn-modal-cancel disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Fermer
                        </button>
                        <button
                          onClick={handleValidate}
                          disabled={isValidating}
                          className="facturation-btn-modal-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isValidating ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Envoi en cours...
                            </>
                          ) : (
                            <>
                              <PaperAirplaneIcon className="h-4 w-4" />
                              Valider et envoyer a Falco
                            </>
                          )}
                        </button>
                      </>
                    )}
                    {(step === 'success' || step === 'error') && (
                      <button
                        onClick={onClose}
                        className="facturation-btn-modal-primary"
                      >
                        Fermer
                      </button>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>

      {/* PDF Preview Modal */}
      {showPdfPreview && generatedInvoice && (
        <PdfPreviewModal
          document={{
            type: 'invoice',
            id: generatedInvoice.id,
            number: generatedInvoice.invoice_number || 'Brouillon',
            date: generatedInvoice.invoice_date,
            clientName: generatedInvoice.billing_name || '-',
            amount: generatedInvoice.total_tvac,
            status: generatedInvoice.status,
            hasPdf: !!generatedInvoice.pdf_path || isPdfPreviewMode,
          }}
          onClose={() => {
            setShowPdfPreview(false);
            setIsPdfPreviewMode(false);
          }}
          onDownload={handleDownloadPdf}
          previewMode={isPdfPreviewMode}
          onBack={() => {
            setShowPdfPreview(false);
            setIsPdfPreviewMode(false);
          }}
          backLabel="Retour a la validation"
        />
      )}
    </>
    </ModalErrorBoundary>
  );
};

export default GenerateSingleInvoiceModal;
